import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { Select } from "@components/forms/select";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  IGetMyRaffleAffiliationsResponseDTO,
  IMyRaffleAffiliationDTO,
} from "@hooks/dtos/raffles/IRaffleAffiliateDTO";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { RaffleAffiliateStatusTypes } from "@hooks/enums/raffle_affiliate";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import {
  getRaffleAffiliateStatus,
  getRaffleStatus,
  raffleAffiliateStatusSelect,
  raffleStatusSelect,
} from "@hooks/functions/Raffle/status";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { FaBroom, FaExpand, FaEye, FaKey, FaMinus } from "react-icons/fa6";
import { Link } from "react-router-dom";

interface IFilters {
  search: string;
  affiliateStatus: RaffleAffiliateStatusTypes | "";
  raffleStatus: RaffleStatusTypes | "";
  startAt: string;
  endAt: string;
}

const emptyFilters: IFilters = {
  search: "",
  affiliateStatus: "",
  raffleStatus: "",
  startAt: "",
  endAt: "",
};

export function MyRaffleAffiliations() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<IFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<IFilters>(emptyFilters);
  const [affiliations, setAffiliations] = useState<IMyRaffleAffiliationDTO[]>(
    [],
  );
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadAffiliations = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        take: take.toString(),
        order,
      });
      if (appliedFilters.search.trim())
        params.set("search", appliedFilters.search.trim());
      if (appliedFilters.affiliateStatus)
        params.set("affiliateStatus", appliedFilters.affiliateStatus);
      if (appliedFilters.raffleStatus)
        params.set("raffleStatus", appliedFilters.raffleStatus);
      if (appliedFilters.startAt) params.set("startAt", appliedFilters.startAt);
      if (appliedFilters.endAt) params.set("endAt", appliedFilters.endAt);

      const response = await api.get<IGetMyRaffleAffiliationsResponseDTO>(
        "/customer/raffle/my-affiliations?" + params.toString(),
      );
      setAffiliations(response.data.list || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.totalPage || 1);
    } catch (error: any) {
      setAffiliations([]);
      setTotal(0);
      setTotalPages(1);
      pushToastify({
        message:
          error?.response?.data?.message ??
          "Não foi possível carregar suas afiliações.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, order, page, pushToastify, reloadToken, take]);

  useEffect(() => {
    void loadAffiliations();
  }, [loadAffiliations]);

  function applyFilters(nextFilters: IFilters = filters) {
    setAppliedFilters({ ...nextFilters });
    setPage(1);
    setReloadToken((current) => current + 1);
  }

  function clearFilters() {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
    setReloadToken((current) => current + 1);
  }

  function updateFilter<K extends keyof IFilters>(key: K, value: IFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function updateAndApplyFilter<K extends keyof IFilters>(
    key: K,
    value: IFilters[K],
  ) {
    const nextFilters = {
      ...filters,
      [key]: value,
    } as IFilters;

    setFilters(nextFilters);
    applyFilters(nextFilters);
  }

  return (
    <>
      <Card>
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <div>
              <h4 className="card-title mb-1">Minhas afiliações</h4>
              <p className="card-category mb-0">
                Campanhas em que você participa como afiliado.
              </p>
            </div>
          </div>
        </div>
      </Card>

      <div className="row">
        <div className="col-12">
          <Card>
            <button
              type="button"
              className="card-header border-0 bg-transparent text-start w-100"
              onClick={() => setShowFilters((current) => !current)}
              aria-expanded={showFilters}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="card-title">Filtrar registros</div>
                  <div className="card-category">
                    Localize campanhas, status e período de afiliação.
                  </div>
                </div>
                {showFilters ? (
                  <FaMinus className="fs-5" title="Ocultar filtros" />
                ) : (
                  <FaExpand className="fs-5" title="Exibir filtros" />
                )}
              </div>
            </button>

            <div className={"card-body pt-0 " + (showFilters ? "" : "d-none")}>
              <div className="row gy-3">
                <div className="col-12 col-md-6 col-xl-4">
                  <Input
                    animated
                    label="Buscar campanha"
                    placeholder="Nome, código ou descrição"
                    type="search"
                    value={filters.search}
                    onChange={(event) =>
                      updateFilter("search", event.target.value)
                    }
                    onKeyUp={(event) => event.key === "Enter" && applyFilters()}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-4">
                  <Select
                    animated
                    label="Status da afiliação"
                    value={filters.affiliateStatus}
                    options={[
                      { value: "", name: "Todos" },
                      ...raffleAffiliateStatusSelect.map((status) => ({
                        value: status.status,
                        name: status.text,
                      })),
                    ]}
                    onChange={(event) => {
                      updateAndApplyFilter(
                        "affiliateStatus",
                        event.target.value as RaffleAffiliateStatusTypes | "",
                      );
                    }}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-4">
                  <Select
                    animated
                    label="Status da campanha"
                    value={filters.raffleStatus}
                    options={[
                      { value: "", name: "Todos" },
                      ...raffleStatusSelect.map((status) => ({
                        value: status.status,
                        name: status.text,
                      })),
                    ]}
                    onChange={(event) => {
                      updateAndApplyFilter(
                        "raffleStatus",
                        event.target.value as RaffleStatusTypes | "",
                      );
                    }}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Input
                    animated
                    id="affiliationStartAt"
                    label="Data inicial da afiliação"
                    type="date"
                    value={filters.startAt}
                    onChange={(event) =>
                      updateFilter("startAt", event.target.value)
                    }
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Input
                    animated
                    id="affiliationEndAt"
                    label="Data final da afiliação"
                    type="date"
                    value={filters.endAt}
                    onChange={(event) =>
                      updateFilter("endAt", event.target.value)
                    }
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-warning text-white"
                  onClick={clearFilters}
                  title="Limpar todos os filtros"
                >
                  <FaBroom />
                </button>
                <button
                  type="button"
                  className="btn btn-success px-4"
                  onClick={() => applyFilters()}
                >
                  Filtrar
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card>
        <div className="card-header">
          <div className="row align-items-center gy-3">
            <div className="col-12 col-lg">
              <h4 className="card-title mb-1">Campanhas afiliadas</h4>
              <p className="card-category mb-0">
                {total} registro{total === 1 ? "" : "s"} encontrado
                {total === 1 ? "" : "s"}.
              </p>
            </div>
            <div className="col-12 col-lg-auto">
              <div className="d-flex flex-column flex-sm-row gap-2">
                <select
                  className="form-select"
                  aria-label="Quantidade de registros por página"
                  value={take}
                  onChange={(event) => {
                    setTake(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  <option value="10">10 por página</option>
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </select>
                <select
                  className="form-select"
                  aria-label="Ordenação das afiliações"
                  value={order}
                  onChange={(event) => {
                    setOrder(event.target.value as "ASC" | "DESC");
                    setPage(1);
                  }}
                >
                  <option value="DESC">Mais recentes</option>
                  <option value="ASC">Mais antigas</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">Campanha</th>
                  <th scope="col">Proprietário</th>
                  <th scope="col" className="text-center">
                    Comissão
                  </th>
                  <th scope="col">Afiliação</th>
                  <th scope="col">Campanha</th>
                  <th scope="col">Desde</th>
                  <th scope="col" className="text-end">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <LoadingSkeleton />
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  affiliations.map((affiliation) => {
                    const affiliateStatus = getRaffleAffiliateStatus(
                      affiliation.status,
                    );
                    const raffleStatus = getRaffleStatus(
                      affiliation.raffleData.status,
                    );
                    const owner = affiliation.raffleData.customerData;
                    const raffleUrl =
                      "/raffle/default/visit?campaign=" +
                      encodeURIComponent(affiliation.raffleData.campaign) +
                      (affiliation.raffleData.key
                        ? "&key=" +
                          encodeURIComponent(affiliation.raffleData.key)
                        : "");

                    return (
                      <tr key={affiliation.id}>
                        <td>
                          <div className="fw-semibold mb-1">
                            <Link
                              to={raffleUrl}
                              className="text-decoration-none text-dark"
                              title="Abrir campanha"
                            >
                              {affiliation.raffleData.name}
                            </Link>
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <Link
                              to={raffleUrl}
                              className="d-inline-flex align-items-center rounded border bg-light text-primary text-decoration-none px-2 py-1 small fw-semibold"
                              title="Abrir campanha pelo código"
                            >
                              #{affiliation.raffleData.campaign}
                            </Link>
                            <Link
                              to={raffleUrl}
                              className="d-inline-flex align-items-center rounded border bg-light text-dark text-decoration-none px-2 py-1 small"
                              title="Abrir campanha pela chave"
                            >
                              <FaKey className="me-1" />
                              {affiliation.raffleData.key ||
                                "Chave não informada"}
                            </Link>
                          </div>
                          {affiliation.raffleData.description_short && (
                            <div
                              className="small text-muted text-truncate"
                              style={{ maxWidth: "20rem" }}
                              title={affiliation.raffleData.description_short}
                            >
                              {affiliation.raffleData.description_short}
                            </div>
                          )}
                        </td>
                        <td>
                          {owner ? (
                            <>
                              <div title={formatName(owner.name)}>
                                {abbreviateName(formatName(owner.name), 15)}
                              </div>
                              <Link
                                to={
                                  "/profile?atsign=" +
                                  encodeURIComponent(owner.atSign)
                                }
                                className="badge bg-primary text-white text-decoration-none"
                                title={"Ver perfil de @" + owner.atSign}
                              >
                                @{owner.atSign}
                              </Link>
                            </>
                          ) : (
                            <span className="text-muted">Não informado</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span
                            className="fw-semibold"
                            title="Percentual recebido pelas vendas como afiliado"
                          >
                            {affiliation.percent}%
                          </span>
                        </td>
                        <td>
                          <span
                            className={
                              "badge bg-" +
                              affiliateStatus.color +
                              " text-white"
                            }
                            title={affiliateStatus.text}
                          >
                            {affiliateStatus.title}
                          </span>
                        </td>
                        <td>
                          <span
                            className={
                              "badge bg-" + raffleStatus.color + " text-white"
                            }
                            title={raffleStatus.title}
                          >
                            {raffleStatus.text}
                          </span>
                        </td>
                        <td title="Data em que a afiliação foi criada">
                          {dayjs(affiliation.createdAt).format(
                            "DD/MM/YYYY [às] HH:mm",
                          )}
                        </td>
                        <td className="text-end">
                          <Link
                            to={raffleUrl}
                            className="btn btn-sm btn-primary"
                            title="Visualizar campanha"
                            aria-label={
                              "Visualizar " + affiliation.raffleData.name
                            }
                          >
                            <FaEye />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}

                {!isLoading && affiliations.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      Nenhuma afiliação encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && totalPages > 1 && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mt-4 pt-3 border-top">
              <span className="small text-muted">
                Página {page} de {totalPages}.
              </span>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={5}
              />
            </div>
          )}
        </div>
      </Card>
    </>
  );
}

